import {useCallback, useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {ButtonAddTable, ToggleSwitch} from "components/Button";
import {DashboardLayout} from "components/Layout";
import {ModalConfirmation} from "components/Modal";
import {BasicTable, SearchBox} from "components/Table";
import {
	useDeleteCustomPaymentMethod,
	useCustomPaymentMethods,
	useEditCustomPaymentMethod,
} from "hooks/SuperAdmin/PaymentMethod";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat} from "utils/converter";

export function CustomPaymentSettingListPage() {
	const history = useHistory();
	const {data, isLoading} = useCustomPaymentMethods();
	const {mutate: deletePaymentMethod, isLoading: isDeleting} = useDeleteCustomPaymentMethod();
	const {mutate: editPaymentMethod} = useEditCustomPaymentMethod();
	const {showToast} = useToast();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteItem, setDeleteItem] = useState({id: null, name: ""});
	const [updatingStatusId, setUpdatingStatusId] = useState(null);

	const tableData = useMemo(() => {
		return (
			data?.data?.map((item, index) => ({
				...item,
				idSerial: index + 1,
				hospitalNames: item?.hospitals?.length ? item.hospitals.map(hospital => hospital.name).join(", ") : "-",
				statusLabel: item?.active ? "Aktif" : "Tidak Aktif",
			})) || []
		);
	}, [data]);

	const handleToggleStatus = useCallback(
		rowData => {
			if (updatingStatusId === rowData?.id) return;

			const hospitalIds = (rowData?.hospitals || []).map(item => item?.id).filter(Boolean);
			const payload = {
				name: rowData?.name,
				active: !rowData?.active,
			};

			if (hospitalIds.length > 0) {
				payload.hospitalIds = hospitalIds;
			}

			setUpdatingStatusId(rowData?.id);

			editPaymentMethod(
				{id: rowData?.id, payload},
				{
					onSuccess: () => {
						showToast(
							"success",
							`Data "${rowData?.name}" berhasil di${rowData?.active ? "nonaktifkan" : "aktifkan"}`,
							3000,
						);
					},
					onError: res => {
						showToast(
							"error",
							convertErrorMessageFormat(res.response.status, res.response.data.message),
							null,
						);
					},
					onSettled: () => {
						setUpdatingStatusId(null);
					},
				},
			);
		},
		[editPaymentMethod, showToast, updatingStatusId],
	);

	const columns = useMemo(
		() => [
			{
				Header: "Nama",
				accessor: "name",
				minWidth: 180,
			},
			{
				Header: "Rumah Sakit",
				accessor: "hospitalNames",
				minWidth: 220,
			},
			{
				Header: "Status",
				accessor: "statusLabel",
				Cell: ({row}) => (
					<ToggleSwitch isActive={!!row.original.active} onChange={() => handleToggleStatus(row.original)} />
				),
			},
			{
				Header: "Action",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							className="text-orange wait-pay"
							style={{cursor: "pointer"}}
							title="Edit"
							onClick={() => history.push(`/custom-payment-setting/edit/${row.original.id}`)}>
							<span className="icon-ico-pencil ft-18" />
						</a>
						<a
							className="text-orange wait-pay"
							style={{cursor: "pointer"}}
							title="Hapus"
							onClick={() => {
								setDeleteItem({id: row.original.id, name: row.original.name || ""});
								setShowDeleteModal(true);
							}}>
							<span className="icon-ico-delete ft-18" />
						</a>
					</>
				),
				centered: true,
				width: 100,
			},
		],
		[handleToggleStatus, history],
	);

	const handleDelete = () => {
		if (!deleteItem.id) return;

		deletePaymentMethod(deleteItem.id, {
			onSuccess: () => {
				showToast("success", `Data "${deleteItem.name}" berhasil dihapus`, 3000);
				setShowDeleteModal(false);
				setDeleteItem({id: null, name: ""});
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	};

	return (
		<DashboardLayout>
			<>
				<ModalConfirmation
					approveLabel="Ya"
					description={`Apakah Anda yakin ingin menghapus data "${deleteItem.name}"?`}
					loading={isDeleting}
					rejectLabel="Batal"
					visible={showDeleteModal}
					onApprove={handleDelete}
					onClose={() => {
						setShowDeleteModal(false);
						setDeleteItem({id: null, name: ""});
					}}
					onReject={() => {
						setShowDeleteModal(false);
						setDeleteItem({id: null, name: ""});
					}}
				/>
				<div className="text-bold text-dark mb-4">Custom Payment Setting</div>
				<SearchBox
					rightElement={
						<ButtonAddTable
							text="Add Payment"
							onClick={() => history.push("/custom-payment-setting/add")}
						/>
					}
					onSearch={() => {}}
				/>
				<BasicTable
					columns={columns}
					currentPage={1}
					data={tableData}
					hidePagination={true}
					limit={10}
					loading={isLoading}
					striped={true}
					title="Custom Payment Setting"
					totalItems={tableData.length}
					totalPage={1}
					onLimitChange={() => {}}
					onPageChange={() => {}}
				/>
			</>
		</DashboardLayout>
	);
}
